import { Dashboard } from "@/components/dashboard/Dashboard/Dashboard";
import { VStack } from "@/components/layout/VStack";
import { HStack } from "@/components/layout/HStack";
import { Input } from "@/components/ui/Input/Input";
import { Label } from "@/components/ui/Label/Label";
import { Switch } from "@/components/ui/Switch/Switch";
import { useState } from "react";
import { useExports } from "../hooks/exports/useExports";
import { ExportCard } from "../components/exports/ExportCard";

const todayYMD = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const addDaysYMD = (base: string, days: number) => {
  const d = new Date(`${base}T00:00:00`);
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const Exports = () => {
  const {
    loading,
    downloadData,
    downloadExplorationsOnDate,
    downloadQuarterlySummary,
    downloadUpcomingExplorations,
  } = useExports();

  const [dataFrom, setDataFrom] = useState("");
  const [dataTo, setDataTo] = useState("");
  const [dataRotationId, setDataRotationId] = useState("");
  const [dataReferencedOnly, setDataReferencedOnly] = useState(false);

  const today = todayYMD();
  const [onDateDate, setOnDateDate] = useState(today);
  const [onDateSessionNames, setOnDateSessionNames] = useState("E1,E2");

  const [upcomingStart, setUpcomingStart] = useState(today);
  const [upcomingEnd, setUpcomingEnd] = useState(addDaysYMD(today, 29));

  return (
    <Dashboard activePage="/exports">
      <VStack>
        <h2
          style={{
            fontSize: "var(--text-2xl)",
            lineHeight: "var(--line-height-2xl)",
            fontWeight: "var(--font-bold)",
          }}
        >
          Exports
        </h2>
        <p style={{ color: "var(--muted-foreground)" }}>
          One-click data exports for system administrators.
        </p>
      </VStack>

      <ExportCard
        title="Full data dump"
        description="Download a JSON snapshot of rotations, sessions, explorations, classes, attendance, enrollments, and users."
        buttonLabel="Download JSON"
        loading={loading.data}
        onDownload={() =>
          downloadData({
            rotationId: dataRotationId.trim() || undefined,
            from: dataFrom || undefined,
            to: dataTo || undefined,
            referencedUsersOnly: dataReferencedOnly,
          })
        }
      >
        <VStack gap="var(--space-sm)">
          <HStack gap="var(--space-sm)" wrap>
            <VStack gap="var(--space-3xs)">
              <Label htmlFor="data-from">From</Label>
              <Input
                id="data-from"
                type="date"
                value={dataFrom}
                onChange={(e) => setDataFrom(e.target.value)}
              />
            </VStack>
            <VStack gap="var(--space-3xs)">
              <Label htmlFor="data-to">To</Label>
              <Input
                id="data-to"
                type="date"
                value={dataTo}
                onChange={(e) => setDataTo(e.target.value)}
              />
            </VStack>
            <VStack gap="var(--space-3xs)">
              <Label htmlFor="data-rotation">Rotation ID (optional)</Label>
              <Input
                id="data-rotation"
                type="text"
                value={dataRotationId}
                onChange={(e) => setDataRotationId(e.target.value)}
                placeholder="ObjectId"
              />
            </VStack>
          </HStack>
          <HStack gap="var(--space-xs)" align="center">
            <Switch
              id="data-referenced"
              checked={dataReferencedOnly}
              onCheckedChange={setDataReferencedOnly}
            />
            <Label htmlFor="data-referenced">
              Only include referenced users
            </Label>
          </HStack>
        </VStack>
      </ExportCard>

      <ExportCard
        title="Explorations on a date"
        description="CSV listing the explorations meeting on a specific date for the chosen session names."
        buttonLabel="Download CSV"
        loading={loading.explorationsOnDate}
        disabled={!onDateDate}
        onDownload={() =>
          downloadExplorationsOnDate({
            date: onDateDate,
            sessionNames: onDateSessionNames.trim() || undefined,
          })
        }
      >
        <HStack gap="var(--space-sm)" wrap>
          <VStack gap="var(--space-3xs)">
            <Label htmlFor="ond-date">Date</Label>
            <Input
              id="ond-date"
              type="date"
              value={onDateDate}
              onChange={(e) => setOnDateDate(e.target.value)}
            />
          </VStack>
          <VStack gap="var(--space-3xs)">
            <Label htmlFor="ond-names">Session names</Label>
            <Input
              id="ond-names"
              type="text"
              value={onDateSessionNames}
              onChange={(e) => setOnDateSessionNames(e.target.value)}
              placeholder="E1,E2"
            />
          </VStack>
        </HStack>
      </ExportCard>

      <ExportCard
        title="Quarterly student summary"
        description="JSON per-student attendance + rubric across Q1–Q4, capped at year-to-date."
        buttonLabel="Download JSON"
        loading={loading.quarterlySummary}
        onDownload={() => downloadQuarterlySummary()}
      />

      <ExportCard
        title="Upcoming explorations"
        description="CSV of upcoming explorations in a date window with current enrollment counts."
        buttonLabel="Download CSV"
        loading={loading.upcoming}
        disabled={!upcomingStart || !upcomingEnd}
        onDownload={() =>
          downloadUpcomingExplorations({
            start: upcomingStart || undefined,
            end: upcomingEnd || undefined,
          })
        }
      >
        <HStack gap="var(--space-sm)" wrap>
          <VStack gap="var(--space-3xs)">
            <Label htmlFor="upc-start">Start</Label>
            <Input
              id="upc-start"
              type="date"
              value={upcomingStart}
              onChange={(e) => setUpcomingStart(e.target.value)}
            />
          </VStack>
          <VStack gap="var(--space-3xs)">
            <Label htmlFor="upc-end">End</Label>
            <Input
              id="upc-end"
              type="date"
              value={upcomingEnd}
              onChange={(e) => setUpcomingEnd(e.target.value)}
            />
          </VStack>
        </HStack>
      </ExportCard>
    </Dashboard>
  );
};

export default Exports;
